import type { ReactNode } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '../lib/utils';

const defaultComponents: Components = {
  h1: ({ children }) => <h1 className="text-3xl font-semibold tracking-tight">{children}</h1>,
  h2: ({ children }) => <h2 className="pt-2 text-2xl font-semibold">{children}</h2>,
  h3: ({ children }) => <h3 className="pt-2 text-xl font-semibold">{children}</h3>,
  p: ({ children }) => <p className="text-sm leading-relaxed text-foreground">{children}</p>,
  ul: ({ children }) => <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">{children}</ul>,
  ol: ({ children }) => (
    <ol className="list-decimal space-y-1 pl-5 text-sm text-foreground">{children}</ol>
  ),
  li: ({ children }) => <li>{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-border pl-3 italic text-muted-foreground">
      {children}
    </blockquote>
  ),
  a: ({ children, href }) => (
    <a
      className="text-primary underline underline-offset-4"
      href={href}
      target="_blank"
      rel="noreferrer"
    >
      {children}
    </a>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse border border-input text-sm">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border border-input bg-muted px-2 py-1 text-left font-semibold">{children}</th>
  ),
  td: ({ children }) => <td className="border border-input px-2 py-1">{children}</td>,
  code: ({ children }) => (
    <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">{children}</code>
  ),
  pre: ({ children }) => (
    <pre className="overflow-x-auto rounded-md bg-muted p-3 font-mono text-xs">{children}</pre>
  ),
  hr: () => <hr className="border-border" />,
};

export type MarkdownRendererProps = {
  markdown: string;
  className?: string;
  emptyState?: ReactNode;
  components?: Components;
};

export function MarkdownRenderer({
  markdown,
  className,
  emptyState = null,
  components,
}: MarkdownRendererProps) {
  if (!markdown.trim()) {
    return <>{emptyState}</>;
  }

  return (
    <div className={cn('space-y-3', className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components ?? defaultComponents}>
        {markdown}
      </ReactMarkdown>
    </div>
  );
}
